/**
 * What a detection-zone type IS — the frontend mirror of applicaite/zone_roles.py.
 *
 * Two axes, because a zone type is really two facts:
 *   ROLE     what the person in the zone is doing
 *   SERVICE  which service counter the zone belongs to, if any
 *
 * Every service counter is three zones: somewhere to queue, somewhere to be served, and
 * somewhere the employee stands.
 *
 * KEPT IN STEP BY A TEST, not by discipline: backend/applicaite/tests/test_zone_roles.py
 * reads this file and fails if it disagrees with the Python map. The pairing rules drive
 * both the settings dropdown here and the ETL there, so a drift between them would pair
 * a desk with the wrong counter and nothing would look broken.
 */

export const ROLE = {
  1: 'traffic',       // Entrance
  2: 'traffic',       // Exit
  3: 'traffic',       // Combined Entrance/Exit
  4: 'waiting',       // Checkout Waiting
  5: 'interaction',   // Checkout Interaction
  6: 'browse',        // Product
  7: 'browse',        // Demo
  8: 'browse',        // Display
  9: 'interaction',   // Info     — NOT waiting: the time IS the service
  10: 'interaction',  // Service  — NOT waiting
  11: 'interaction',  // Pick Up  — NOT waiting
  12: 'waiting',      // Info Waiting
  13: 'waiting',      // Service Waiting
  14: 'waiting',      // Pick Up Waiting
  15: 'staff',        // Info Desk
  16: 'staff',        // Service Desk
  17: 'staff',        // Pick Up Desk
  999: 'staff',       // Cash Register
}

export const SERVICE = {
  4: 'checkout', 5: 'checkout', 999: 'checkout',
  9: 'info', 12: 'info', 15: 'info',
  10: 'service', 13: 'service', 16: 'service',
  11: 'pickup', 14: 'pickup', 17: 'pickup',
}

export const NAMES = {
  1: 'Entrance Zone', 2: 'Exit Zone', 3: 'Combined Entrance Exit Zone',
  4: 'Checkout Waiting Zone', 5: 'Checkout Interaction Zone',
  6: 'Product Zone', 7: 'Demo Zone', 8: 'Display Zone',
  9: 'Info Zone', 10: 'Service Zone', 11: 'Pick Up Zone',
  12: 'Info Waiting Zone', 13: 'Service Waiting Zone', 14: 'Pick Up Waiting Zone',
  15: 'Info Desk Zone', 16: 'Service Desk Zone', 17: 'Pick Up Desk Zone',
  999: 'Cash Register Zone',
}

export const roleOf = (typeId) => ROLE[typeId] || null
export const serviceOf = (typeId) => SERVICE[typeId] || null
export const nameOf = (typeId) => NAMES[typeId] || String(typeId)
export const isRole = (typeId, role) => roleOf(typeId) === role

export const typesWithRole = (role) =>
  Object.keys(ROLE).map(Number).filter((t) => ROLE[t] === role).sort((a, b) => a - b)

/**
 * The types serving the SAME counter in a different role.
 *   counterpartTypes(15, 'interaction') -> [9]   the Info Zone an Info Desk serves
 *   counterpartTypes(5, 'staff')        -> [999] the register behind that checkout
 *
 * This is what stops a desk being paired across services: an Info Desk Zone can only
 * ever offer Info Zones, because they are the only ones sharing its service.
 */
export const counterpartTypes = (typeId, role) => {
  const svc = SERVICE[typeId]
  if (!svc) return []
  return Object.keys(SERVICE)
    .map(Number)
    .filter((t) => SERVICE[t] === svc && ROLE[t] === role)
    .sort((a, b) => a - b)
}
